import QueryString from "query-string";
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  fetchAiWalletsSuccess,
  fetchAiWalletsFailure,
  fetchAiLedgerSuccess,
  fetchAiLedgerFailure,
  fetchAiPlansSuccess,
  fetchAiPlansFailure,
  startAiCheckoutSuccess,
  startAiCheckoutFailure,
  completeAiCheckoutSuccess,
  completeAiCheckoutFailure,
  fetchAiWallets,
  fetchAiLedger,
} from "./actions";
import {
  FETCH_AI_WALLETS,
  FETCH_AI_LEDGER,
  FETCH_AI_PLANS,
  START_AI_CHECKOUT,
  COMPLETE_AI_CHECKOUT,
} from "./constants";

export function* fetchAiWalletsSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(api(token).get, `/api/ai-credits?${query}`);
    yield put(fetchAiWalletsSuccess(data));
  } catch (e) {
    yield put(fetchAiWalletsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while fetching AI credit balances!"));
  }
}

export function* fetchAiLedgerSaga({ payload, append = false }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const res = yield call(api(token).get, `/api/ai-credits/ledger?${query}`);
    yield put(fetchAiLedgerSuccess(res, append));
  } catch (e) {
    yield put(fetchAiLedgerFailure(null, append));
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while fetching AI credit history!"));
  }
}

export function* fetchAiPlansSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/ai-credits/plans?${query}`,
    );
    yield put(fetchAiPlansSuccess(data));
  } catch (e) {
    yield put(fetchAiPlansFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while fetching credit plans!"));
  }
}

export function* startAiCheckoutSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/ai-credits/checkout`,
      payload,
    );
    yield put(startAiCheckoutSuccess(data));
    if (data?.checkout_url) {
      window.location.assign(data.checkout_url);
    } else {
      yield put(errorAlert("Stripe did not return a checkout URL."));
    }
  } catch (e) {
    yield put(startAiCheckoutFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while starting payment!"));
  }
}

export function* completeAiCheckoutSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const locationId = payload?.location_id;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/ai-credits/checkout/complete`,
      { session_id: payload?.session_id },
    );
    yield put(completeAiCheckoutSuccess(data));
    if (data?.status === "succeeded" && data?.granted) {
      const label =
        data.category === "general" ? "General AI" : "Care Planning";
      yield put(successAlert(`${data.credits} ${label} credits added.`));
    } else if (data?.status === "succeeded") {
      yield put(successAlert("Payment received. Credits are up to date."));
    }
    if (locationId) {
      yield put(fetchAiWallets({ location_id: locationId }));
      yield put(fetchAiLedger({ location_id: locationId, limit: 20 }));
    }
  } catch (e) {
    yield put(completeAiCheckoutFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Could not confirm the payment."));
  }
}

export default function* AICreditsSaga() {
  yield all([
    takeLatest(FETCH_AI_WALLETS, fetchAiWalletsSaga),
    takeLatest(FETCH_AI_LEDGER, fetchAiLedgerSaga),
    takeLatest(FETCH_AI_PLANS, fetchAiPlansSaga),
    takeLatest(START_AI_CHECKOUT, startAiCheckoutSaga),
    takeLatest(COMPLETE_AI_CHECKOUT, completeAiCheckoutSaga),
  ]);
}
